/**
 *  Copyright (c) 2019-present The TVM Engineering Authors.
 *
 * @flow
 */

import React, { useState, useEffect, memo, useRef } from "react";
import { useDispatch } from "react-redux";
import Grid from "@mui/material/Grid";

import { createStyles, makeStyles } from "@mui/styles";

import {
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Box,
  Button,
} from "@mui/material";
import { Markup } from "interweave";
import DataTableComponent from "./DataTableComponent";
import { textLabelTable, ToolsColumns } from "./data";
import i18n from "../../services/i18n";
import Section from "../widgets/Section";
import { getDateTime } from "../../utils/misc";
import DownloadIcon from "../../assets/img/download-icon.png";
import DownloadIconBlue from "../../assets/img/download-icon-blue.png";
import { fileDownload } from "../../services/files";
import { sendMailForDownloadFile } from "../../services/users";
import { downloadLog } from "../../services/downloadLog";
import useExitPrompt from "../../utils/useExitPrompt";
import { getTermsById } from "../../services/termsVersion";
import { toast_notifications } from "../../utils/notifications";
import getTheUserState from "../../utils/userState";

const user = getTheUserState();

const useStyles = makeStyles(() =>
  createStyles({
    tableWrapper: {
      "& .MuiPaper-elevation3": {
        boxShadow: "unset",
        padding: "0",
      },
      "& thead tr th div": {
        color: "#1f577b",
        fontSize: "1.1rem",
        fontWeight: "500",
      },
      "& tbody tr td div": {
        color: "#5690b5",
        fontSize: "0.875rem",
      },
    },
    toolName: {
      lineHeight: "1",
      position: "relative",
      paddingLeft: "35px",
      color: "gray",
      cursor: "not-allowed",
      "&:before": {
        content: "''",
        display: "inline-block",
        width: "15px",
        height: "15px",
        backgroundImage: `url(${DownloadIcon})`,
        color: "red",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        position: "absolute",
        top: "1px",
        left: "0px",
      },
    },
    toolNameActive: {
      lineHeight: "1",
      position: "relative",
      paddingLeft: "35px",
      cursor: "pointer",
      "&:before": {
        content: "''",
        display: "inline-block",
        width: "15px",
        height: "15px",
        backgroundImage: `url(${DownloadIconBlue})`,
        color: "red",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        position: "absolute",
        top: "1px",
        left: "0px",
      },
    },
    toolsTable: {
      padding: "15px",
      "& table thead tr th:first-child": {
        display: "none",
      },
      "& table tbody tr td:first-child": {
        display: "none",
      },
    },
    popUp: {
      "& div": {
        borderRadius: "0",
      },
    },
    popUpCondent: {
      maxHeight: "750px",
      overflowY: "scroll",

      "& #scroll-dialog-description": {
        minHeight: "751px",
      },
    },
    btn: {
      color: "#fff",
      background: "#147AAE ",

      "&:hover": {
        background: "#0b98cf",
      },
      "&:disabled": {
        background: "#c0c0c054",
      },
    },
    buttonsContainer: {
      display: "flex",
      justifyContent: "space-between",
    },
    progressWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-around",
      minWidth: "251px",
    },
    progressCounter: {
      position: "relative",
      display: "inline-flex",
      marginRight: "5vh",
    },
  })
);
interface Props {
  tools: () => void;
  project: () => void;
}

const ToolsTable = memo((props: Props) => {
  const { project, tools } = props;

  const [entries, setEntries] = useState([]);
  const [disabledConfirmBtn, setDisabledConfirmBtn] = useState(true);
  const [selectedToolFromTable, setSelectedToolFromTable] = useState(false);
  const [selectedTermsFromTable, setSelectedTermsFromTable] = useState(false);
  const [open, setOpen] = useState(false);
  const [scroll, setScroll] = useState("paper");
  const [fileDownloadProgress, setFileDownloadProgress] = useState(0);
  const [isCancelBtnDisabled, setIsCancelBtnDisaled] = useState(false);
  const [showExitPrompt, setShowExitPrompt] = useExitPrompt(false);

  const handleClick = (e) => {
    e.preventDefault();
    setShowExitPrompt(!showExitPrompt);
  };

  const classes = useStyles();
  const dispatch = useDispatch();

  useEffect(() => {
    if (tools?.length !== 0 && Array.isArray(tools)) {
      const arr = [];
      tools.map((item) => {
        return arr.push({
          id: item.id,
          tool_name: item.tool_name,
          description: item.description,
          status: item.status ? "Active" : "Pending / Inactive",
          serial_id: item.serial_id,
          terms: item.terms,
          time_to_use: getDateTime(item.time_to_use),
          terms_and_condition_id: item.terms_and_condition_id,
          filename: item.upload_path?.split("\\")?.pop()?.split("/").pop(),
          created: getDateTime(item.createdAt),
        });
      });
      setEntries(arr);
    } else {
      setEntries([]);
    }
  }, [tools]);

  // useEffect(() => {
  // 	if(fileDownloadProgress === 100){
  // 		setTimeout(() => {
  // 			setOpen(false)
  // 		},2000)
  // 	}
  // }, [fileDownloadProgress])

  const descriptionElementRef = useRef(null);
  useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  const handleClose = () => {
    setShowExitPrompt(false);
    setOpen(false);
    setFileDownloadProgress(0);
  };

  useEffect(() => {
    if (fileDownloadProgress === 100) {
      handleClose();
    }
  }, [fileDownloadProgress]);

  function downloadLink(value, tableMeta) {
    const showPopUp = () => {
      setShowExitPrompt(true);
      setOpen(true);
      setScroll("paper");
      setDisabledConfirmBtn(true);
      // setSelectedToolFromTable( entries.filter((entry) => entry.id === tableMeta.rowData[0]) );
      entries.map(async (entry) => {
        if (entry.id === tableMeta.rowData[0]) {
          setSelectedToolFromTable(entry);
          const terms = await dispatch(
            getTermsById({ terms_and_condition_id: entry.terms_and_condition_id })
          );
          setSelectedTermsFromTable(terms);
        }
        return true;
      });
    };

    const saveFile = async () => {
      setIsCancelBtnDisaled(true);
      const directory = "tools";
      // setOpen(false);
      const isDownloadedFile = await dispatch(
        fileDownload(selectedToolFromTable.filename, setFileDownloadProgress, directory)
      );

      if (isDownloadedFile) {
        await dispatch(
          sendMailForDownloadFile({
            user: user.user,
            selectedTool: selectedToolFromTable,
          })
        );
        await dispatch(
          downloadLog({
            user: user.user,
            selectedProject: project,
            selectedTool: selectedToolFromTable,
          })
        );
      }
      setIsCancelBtnDisaled(false);
    };

    const handleScroll = (e) => {
      const bottom = e.target.scrollHeight - e.target.scrollTop - 5 <= e.target.clientHeight;
      if (bottom) {
        setDisabledConfirmBtn(false);
      }
    };

    return (
      <Typography>
        {tableMeta.rowData[3] === "Active" && (
          <Typography component="p" className={classes.toolNameActive} onClick={showPopUp}>
            {value}
          </Typography>
        )}
        {tableMeta.rowData[3] === "Pending / Inactive" && (
          <Typography
            component="p"
            className={classes.toolName}
            onClick={() =>
              toast_notifications.warning(
                "Selected tool is currently unavailable. Please contact us."
              )
            }
          >
            {value}
          </Typography>
        )}
        <Typography>
          <Dialog
            open={open}
            // onClose={handleClose}
            scroll={scroll}
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
            className={classes.popUp}
          >
            <DialogTitle id="scroll-dialog-title">
              {selectedTermsFromTable.version_name}
              <button type="button" onClick={handleClick} style={{ display: "none" }}>
                Show/Hide the prompt
              </button>
            </DialogTitle>
            <DialogContent
              id="scroll-dialog-content"
              dividers={scroll === "paper"}
              onScroll={handleScroll}
              className={classes.popUpCondent}
            >
              <DialogContentText
                id="scroll-dialog-description"
                ref={descriptionElementRef}
                tabIndex={-1}
              >
                {/* { selectedToolFromTable !== false ? getTerms(selectedToolFromTable.terms) : '' } */}
                {selectedToolFromTable !== false ? (
                  <Markup content={selectedTermsFromTable.content} />
                ) : (
                  ""
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions className={classes.buttonsContainer}>
              <Button
                // className={classes.btn}
                onClick={handleClose}
                disabled={isCancelBtnDisabled}
                sx={{
                  backgroundColor: "gray",
                  color: "#FFF",
                  "&:hover": { backgroundColor: "#656464" },
                }}
              >
                Cancel
              </Button>

              {fileDownloadProgress > 0 ? (
                // <FileProgressIndicator filreProgress={fileDownloadProgress} type='download'/>
                <div className={classes.progressWrapper}>
                  <Typography>Downloading...</Typography>
                  <Box className={classes.progressCounter}>
                    <CircularProgress variant="determinate" value={fileDownloadProgress} />
                    <Box
                      top={0}
                      left={0}
                      bottom={0}
                      right={0}
                      position="absolute"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <b style={{ fontSize: "12px" }}>{fileDownloadProgress}%</b>
                    </Box>
                  </Box>
                </div>
              ) : (
                <Button
                  // className={classes.btn}
                  onClick={saveFile}
                  color="primary"
                  variant="contained"
                  disabled={disabledConfirmBtn}
                >
                  {i18n.t("core:acceptTermsAndDownload")}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </Typography>
      </Typography>
    );
  }

  const columns = ToolsColumns(downloadLink);

  const options = {
    textLabels: textLabelTable,
    filter: true,
    sort: true,
    filterType: "dropdown",
    resizableColumns: false,
    responsive: "standard",
    rowsPerPage: 5,
    rowsPerPageOptions: [5, 10, 25, 50],
    print: false,
    download: false, // hide csv download option
    downloadOptions: {
      filename: "excel-format.csv",
      separator: ";",
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      },
    },
  };

  return (
    <Grid item xs={12} className={classes.tableWrapper}>
      <Typography className={classes.toolsTable}>
        {/* <Section actions={actions}> - Uncomment if you want to add Add User Button in the section. */}
        <Section>
          <DataTableComponent title=" " columns={columns} data={entries} options={options} />
        </Section>
      </Typography>
    </Grid>
  );
});

export default ToolsTable;
